import React, { useEffect, useRef, useState } from 'react'
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap'
import PropTypes from 'prop-types'
import { useRouter } from 'next/router'
import { useLazyQuery, useQuery } from '@apollo/client'

import styles from './style.module.scss'
import useDebounce from '@shared/hooks/useDebounce'
import CustomFormGroup from '@shared/components/customForm/customFormGroup'
import CustomInput from '@shared/components/customForm/customInput'
import { SearchIcon } from '@shared/components/ctIcons'
import { GET_SIMILAR_PLAYERS } from '@graphql/comparePlayer/comparePlayer.query'
import MyImage from '@shared/components/myImage'
import playerImg from '@assets/images/placeholder/Placeholder_Player.svg'
import { GET_SEARCH_PLAYER } from '@graphql/search/search.query'
import { getSearchPayload } from '@pages/search'
import { getImgURL } from '@shared-submodule/utils'

function ComparePlayerPopup({ hide, playerId, slug, isSecond = false, p2Id = '' }) {
  const router = useRouter()
  const [searchVal, setSearchVal] = useState('')
  const [loading, setLoading] = useState(false)
  const [suggestion, setSuggestion] = useState([])

  const { data, loading: isLoading } = useQuery(GET_SIMILAR_PLAYERS, {
    variables: {
      input: {
        iPlayerId: playerId,
        nLimit: 15
      }
    },
    onCompleted: d => {
      setSuggestion(d?.getSimilarPlayers)
    }
  })

  useEffect(() => {
    if (searchVal === '') {
      setSuggestion(data?.getSimilarPlayers)
    }
  }, [searchVal])

  function generateSlugUrl(player2slug) {
    let p1Slug
    let p2Slug
    if (isSecond) {
      p1Slug = slug.split('/')?.[2]
      p2Slug = player2slug.split('/')?.[2]
    } else {
      p1Slug = player2slug.split('/')?.[2]
      p2Slug = slug.split('/')?.[2]
    }
    const finalSlug = `/cricket/players/compare/${p1Slug}-vs-${p2Slug}`
    router.push(finalSlug)
    hide()
  }

  return (
    <Modal
      size="xl"
      show={true}
      onHide={hide}
      aria-labelledby="contained-modal-title-vcenter"
      className={`${styles.modalContainer}`}
      contentClassName={styles.modalContent}
      centered>
      <Form>
        <div className="w-100">
          <SearchBox
            defaultData={data?.data?.getSimilarPlayers?.players || []}
            searchVal={searchVal}
            setSearchVal={setSearchVal}
            setSuggestion={setSuggestion}
            setLoading={setLoading}
          />
        </div>
        {loading || isLoading ? (
          <Modal.Body className={`${styles.modalBody} d-flex justify-content-center h-100`}>
            <Spinner animation="border" size="md" />
          </Modal.Body>
        ) : null}
        {!loading && !isLoading && (
          <Modal.Body className={`${styles.modalBody}`}>
            <Row className="gx-0">
              {suggestion?.map((d, i) => {
                if (d?._id === playerId || d?._id === p2Id) return null
                return (
                  <Col key={i} md={4}>
                    <div className={styles.dropdown_item} onClick={() => generateSlugUrl(d?.oSeo?.sSlug)} rel="noreferrer">
                      <div className={`${styles.image} me-1 rounded-circle overflow-hidden flex-shrink-0`}>
                        <MyImage
                          src={d?.oImg?.sUrl ? getImgURL(d?.oImg?.sUrl) : playerImg}
                          layout="responsive"
                          width="34"
                          height="34"
                          alt="team"
                        />
                      </div>
                      <div className="ms-1 d-flex flex-column">
                        <span className={styles.overflow_clip}> {d?.sFullName}</span>
                        <span className={styles.secondaryText}> {d?.sCountryFull}</span>
                      </div>
                    </div>
                  </Col>
                )
              })}
            </Row>
          </Modal.Body>
        )}
      </Form>
    </Modal>
  )
}

ComparePlayerPopup.propTypes = {
  hide: PropTypes.func,
  playerId: PropTypes.string,
  slug: PropTypes.string,
  isSecond: PropTypes.bool,
  p2Id: PropTypes.string
}

export default ComparePlayerPopup

function SearchBox({ searchVal, setSearchVal, setSuggestion, setLoading }) {
  const search = useRef(null)
  const [searchPlayer] = useLazyQuery(GET_SEARCH_PLAYER)

  function handleClear() {
    setSearchVal('')
  }

  const enhancedSearch = useDebounce(searchVal, 500)

  const changeSearch = async val => {
    try {
      if (!val || val.length < 3) {
        return // or do nothing
      }
      setLoading(true)

      if (!val.endsWith(' ')) {
        // setSearchVal(val)
        const data = await searchPlayer({ variables: { input: getSearchPayload(val, 4) } })
        setSuggestion(data?.data?.getPlayerSearch?.aResults)
        setLoading(false)
      } else {
        setLoading(false)
      }
    } catch (err) {
      setLoading(false)
    }
  }

  useEffect(() => {
    changeSearch(enhancedSearch)
  }, [enhancedSearch])

  useEffect(() => {
    search.current?.focus()
    function searchShortcut(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault()
        search.current.focus()
      } else if (search.current.isSelected && e.key === 'Escape') {
        e.preventDefault()
        search.current.blur()
        handleClear()
      }
    }
    window.addEventListener('keydown', searchShortcut)
    return () => window.removeEventListener('keydown', searchShortcut)
  }, [])

  return (
    <>
      <div className={`align-items-center ${styles.searchBar} w-100`}>
        <CustomFormGroup className={`${styles.searchDiv}  hd-searchGroup w-100 position-relative mb-md-0`} controlId="search">
          <CustomInput
            autoComplete="off"
            value={searchVal}
            ref={search}
            className={`${styles.search} py-3 px-4 hs-searchControl border-0 m-0`}
            type="text"
            placeholder={'Search Player'}
            onChange={e => {
              setSearchVal((e.target.value || '').replace(/\s+/g, ' '))
            }}
          />
          <Button variant="link" className={`${styles.searchGroupBtn} ${styles.icon} ${styles.searchbtn} me-2 small-btn`}>
            <SearchIcon />
          </Button>
        </CustomFormGroup>
      </div>
    </>
  )
}
SearchBox.propTypes = {
  setSuggestion: PropTypes.func,
  setSearchVal: PropTypes.func,
  setLoading: PropTypes.func,
  suggestions: PropTypes.object,
  searchVal: PropTypes.string
}
